import { EnqueueSnackbar } from "notistack";
import { TFunction } from "i18next";

const UNKNOWN_ERROR_MESSAGE = "Unknown error";

export function getBackendMessage(error: unknown): string {
  if (error != null && typeof error === "object" && "response" in error) {
    const r = (error as { response?: { data?: { message?: string } }; message?: string }).response;
    const msg = r?.data?.message ?? (error as { message?: string }).message;
    if (typeof msg === "string") return msg;
    return UNKNOWN_ERROR_MESSAGE;
  }
  if (error != null && typeof error === "object" && "message" in error) {
    const msg = (error as { message: unknown }).message;
    if (typeof msg === "string") return msg;
    return UNKNOWN_ERROR_MESSAGE;
  }
  if (typeof error === "string") return error;
  if (typeof error === "object" && error !== null) return UNKNOWN_ERROR_MESSAGE;
  return String(error);
}

/**
 * Handles fleet request API errors (create/update booking or block, cancel, etc.) and shows a translated snackbar.
 * Accepts unknown so it can be used with CreateResult.error or catch (e).
 */
export function handleFleetRequestError(
  error: unknown,
  enqueueSnackbar: EnqueueSnackbar,
  t: TFunction
): void {
  const backendMessage = getBackendMessage(error);
  const handleErrorSnackbar = (message: string) => enqueueSnackbar(message, { variant: "error" });

  const exactMessageToKey: Record<string, string> = {
    "Cannot cancel a booking that has already started":
      "_fleetBookingEventCannotCancelBookingThatHasAlreadyStarted",
    "You do not have permission to cancel this booking":
      "_fleetBookingEventYouDoNotHavePermissionToCancelThisBooking",
    "You already have a vehicle booking at this time":
      "_fleetBookingEventYouAlreadyHaveAVehicleBookingAtThisTime",
    "User is blocked from fleet bookings": "_fleetBookingEventUserBlockedFromFleetBookings",
    "End time must be after start time": "_fleetBookingEventEndTimeMustBeAfterStartTime",
    "Cannot book in the past": "_fleetBookingEventCannotBookInThePast",
    "Vehicle is already booked for this time period":
      "_fleetBookingEventVehicleAlreadyBookedForThisTimePeriod",
    "No active schedule found for vehicle at this time":
      "_fleetBookingEventNoActiveScheduleForVehicleAtThisTime",
    "You do not have permission to book this vehicle":
      "_fleetBookingEventNoPermissionToBookThisVehicle",
    "There is another booking colliding with your updated booking times.":
      "_fleetBookingEventAnotherBookingCollidingWithUpdatedTimes",
    "There was a conflict with one of your bookings while editing vehicle booking times":
      "_fleetBookingEventConflictWhileEditingVehicleBookingTimes",
    "Please contact admin. Your existing vehicle booking has already been checked in.":
      "_fleetBookingEventContactAdminExistingBookingCheckedIn",
    "End date must be after start date": "_fleetBlockEventEndDateMustBeAfterStartDate"
  };

  const translationKey = exactMessageToKey[backendMessage];
  if (translationKey != null) {
    handleErrorSnackbar(t(translationKey));
    return;
  }

  // Variable messages (backend includes IDs or dates)
  if (backendMessage.includes("Vehicle is blocked from")) {
    handleErrorSnackbar(t("_fleetBookingEventVehicleBlockedForPeriod"));
    return;
  }
  if (backendMessage.includes("Vehicle with ID") && backendMessage.includes("not found")) {
    handleErrorSnackbar(t("_fleetBookingEventVehicleNotFound"));
    return;
  }
  if (backendMessage.includes("Booking with ID") && backendMessage.includes("not found")) {
    handleErrorSnackbar(t("_fleetBookingEventBookingNotFound"));
    return;
  }
  if (backendMessage.includes("Blocking event overlaps with existing blocking")) {
    handleErrorSnackbar(t("_fleetBlockEventOverlapsWithExistingBlocking"));
    return;
  }
  if (backendMessage.includes("Blocking event with ID") && backendMessage.includes("not found")) {
    handleErrorSnackbar(t("_fleetBlockEventNotFound"));
    return;
  }

  handleErrorSnackbar(t("There was a server error"));
}
